import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HERO_SLIDES, WHATSAPP_URL, PLACEHOLDER_IMG } from '../utils/constants';
import './HeroSlider.css';

const AUTOPLAY_MS = 5500;

const HeroSlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused]   = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((i: number): void => {
    setCurrent(((i % HERO_SLIDES.length) + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % HERO_SLIDES.length);
    }, AUTOPLAY_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [paused, current]);

  return (
    <header
      className="hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="hero-ornament" />

      {HERO_SLIDES.map((slide, i) => (
        <div key={slide.title} className={`hero-slide ${i === current ? 'active' : ''}`}>
          <div className="container hero-inner">
            <div className="hero-copy">
              <span className="hero-badge">{slide.badge}</span>
              <h1 className="hero-title">{slide.title}<br /><em>{slide.em}</em></h1>
              <p className="hero-subtitle">{slide.subtitle}</p>
              <div className="hero-actions">
                <button className="btn-gold" onClick={() => navigate('/products')}>Explore Collections</button>
                <a href={WHATSAPP_URL('Hi! I\'d like to enquire about your collection.')}
                   target="_blank" rel="noreferrer" className="btn-outline-wa">
                  💬 WhatsApp Enquiry
                </a>
              </div>
              <div className="hero-stats">
                <div className="hero-stat"><b>196+</b><span>Products</span></div>
                <div className="hero-stat"><b>10+</b><span>Categories</span></div>
              </div>
            </div>

            <div className="hero-visual">
              <svg className="motif" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
                <g fill="none" stroke="#f7c381" strokeWidth="1">
                  <path d="M200 40 Q260 100 240 180 Q220 240 200 260 Q180 240 160 180 Q140 100 200 40Z" />
                  <circle cx="200" cy="150" r="90" />
                  <circle cx="200" cy="150" r="140" />
                </g>
              </svg>
              <img
                src={slide.image} alt={slide.title} loading={i === 0 ? 'eager' : 'lazy'}
                onError={e => { e.currentTarget.src = PLACEHOLDER_IMG; }}
              />
              {slide.tag && <span className="hero-visual-tag">{slide.tag}</span>}
            </div>
          </div>
        </div>
      ))}

      <button className="hero-arrow prev" aria-label="Previous slide" onClick={() => goTo(current - 1)}>‹</button>
      <button className="hero-arrow next" aria-label="Next slide" onClick={() => goTo(current + 1)}>›</button>
      <div className="hero-dots">
        {HERO_SLIDES.map((slide, i) => (
          <button key={slide.title} className={`hero-dot ${i === current ? 'active' : ''}`}
            aria-label={`Slide ${i + 1}`} onClick={() => goTo(i)} />
        ))}
      </div>
    </header>
  );
};

export default HeroSlider;
